<script setup>
  import {computed, inject, provide, ref, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import {Deferred as InertiaDeferred, router, usePage} from "@inertiajs/vue3";
  import LaborTableGfrCalculator from "@pages/Records/Components/Sections/LaborBar/Components/GfrCalculator.vue";
  import MainLaborValues from "@pages/Records/Components/Sections/LaborBar/Components/MainLaborValues.vue";
  import LaborSearch from "@pages/Records/Components/Sections/LaborBar/Components/Search.vue";
  import LaborTable from "@pages/Records/Components/Sections/LaborBar/Components/Table.vue";
  import {mainLaborvalueList} from "@pages/Records/Components/Sections/LaborBar/enums.js";
  import {MedButton, MedInfoButton} from "@pharma4u/medicheck-components";
  import {delayWhileEditing} from "@utils/Helpers/DelayWhileEditing.js";
  import {cloneDeep, debounce} from "es-toolkit";

  defineOptions({name: "RecordComponentSectionLaborBar"});

  const props = defineProps({
    isEditable: {
      type: Boolean,
      require: true,
    },
    changeIsEditable: {
      type: Function,
      required: true,
    },
    recordData: {
      type: Object,
      required: true,
    },
    problems: {
      type: Object,
      required: true,
    },
  });

  const page = usePage();
  const isMergeRecordTransfer = inject("isMergeRecordTransfer");

  const setIsMassUpdateProcessing = inject("setIsMassUpdateProcessing");
  const getIsMassUpdateProcessing = inject("getIsMassUpdateProcessing");

  const refLaborBar = ref(null);
  const refLaborValueGfrDialog = ref(null);

  const modeOfMainLaborValues = ref(null);
  const modeOfOtherLaborValues = ref(null);

  const laborValues = ref(cloneDeep(props.recordData.laborvalues));
  const mainLaborValues = computed(() => {
    return laborValues.value.filter((laborValue) => {
      return mainLaborvalueList.includes(laborValue.mc_laborvalue_key);
    });
  });
  const otherLaborValues = computed(() => {
    return laborValues.value.filter((laborValue) => {
      return !mainLaborvalueList.includes(laborValue.mc_laborvalue_key);
    });
  });
  watch(
    () => mainLaborValues.value,
    (newValue) => {
      if (newValue.length <= 7) {
        // no multi select available
        modeOfMainLaborValues.value = null;
      }
    },
  );

  const isMassUpdateProcessing = computed(() => getIsMassUpdateProcessing("laborValue"));

  const isGenderDiverse = computed(() => page.props.patient.gender === "diverse");

  /**
   * Non-unique names (type) of main laborvalues
   * (size, weight and bmi are grouped)
   */
  const mainLaborValuesWithConflicts = computed(() => {
    // only show conflicts when merging records
    if (!isMergeRecordTransfer.value) return [];

    const conflictingLaborvalues = mainLaborValues.value.filter((item, _, array) => {
      const sameKeyItems = array.filter((i) => i.mc_laborvalue_key === item.mc_laborvalue_key);
      return sameKeyItems.length > 1;
    });

    const bmiKeys = new Set([4, 182, 183]);
    const combinedName = "Größe, Gewicht und BMI";
    const uniqueNames = [];

    for (const item of conflictingLaborvalues) {
      const nameToAdd = !bmiKeys.has(item.mc_laborvalue_key) ? item.type : combinedName;

      if (!uniqueNames.includes(nameToAdd)) {
        uniqueNames.push(nameToAdd);
      }
    }
    return uniqueNames;
  });

  /**
   * Names (type) of laborvalues, that exist multiple times
   * with different deviations.
   */
  const otherLaborValuesWithConflicts = computed(() => {
    const conflictingLaborvalues = otherLaborValues.value
      // filter down duplicates with different deviations
      .filter((item, _, array) => {
        const sameKeyItems = array.filter((i) => i.mc_laborvalue_key === item.mc_laborvalue_key);
        const uniqueDeviations = new Set(sameKeyItems.map((i) => i.deviation));
        return uniqueDeviations.size > 1;
      });

    // unique names
    return [...new Set(conflictingLaborvalues.map((item) => item.type))];
  });

  watch(
    () => props.recordData.laborvalues,
    () => {
      laborValues.value = cloneDeep(props.recordData.laborvalues);
    },
    {deep: true},
  );

  const updateLaborValues = debounce(() => {
    delayWhileEditing(refLaborBar, () => {
      setIsMassUpdateProcessing("laborValue", true);
      router.put(
        route("laborvalues.mass-update-or-create", {
          patient: page.props.patient.id,
          record: page.props.record.id,
        }),
        {
          ...laborValues.value,
        },
        {
          preserveScroll: true,
          only: ["flash", "errors", "symptoms", "laborvalues", "diseases"],
          onFinish: () => {
            setIsMassUpdateProcessing("laborValue", false);
          },
        },
      );
    });
  }, 500);

  const openDialog = (dialogName, data) => {
    switch (dialogName) {
      case "refLaborValueGfrDialog":
        refLaborValueGfrDialog.value.open(data);
        break;
    }
  };

  provide("laborValueOpenDialog", openDialog);

  const toggleModeOfMainLaborValues = (newMode) => {
    modeOfMainLaborValues.value = modeOfMainLaborValues.value === newMode ? null : newMode;
  };

  const toggleModeOfOtherLaborValues = (newMode) => {
    modeOfOtherLaborValues.value = modeOfOtherLaborValues.value === newMode ? null : newMode;
  };
</script>

<template>
  <div class="flex items-center space-x-2 px-2 pb-4">
    <h5 class="text-2xl">Labor- / Vitalwerte</h5>

    <template v-if="!isEditable">
      <MedButton
        variant="primary"
        class="ml-auto"
        :data-test="getTestId('bar-set-editable-button')"
        @click="changeIsEditable"
      >
        Bearbeiten
      </MedButton>
    </template>

    <template v-if="isEditable">
      <MedInfoButton
        size="lg"
        side="top"
        aria-label="Hinweis zu Labor- / Vitalwerten"
        :data-test="getTestId('labor-bar-info-button')"
      >
        <p class="max-w-md text-sm">
          <strong>Hinweis:</strong>
          Nur Labor- / Vitalwerte außerhalb der Norm werden automatisiert geprüft (z.B. ob Arzneimittel für die
          Abweichung verantwortlich sind oder ob Probleme / Symptome mit den Abweichungen in Verbindung stehen könnten).
        </p>
      </MedInfoButton>

      <template v-if="mainLaborValues.length > 7 || modeOfMainLaborValues === 'multi-select'">
        <MedButton
          class="ml-auto"
          :data-test="getTestId('toggle-multi-select-button')"
          @click="toggleModeOfMainLaborValues('multi-select')"
        >
          <template v-if="modeOfMainLaborValues === 'multi-select'">Abbrechen</template>
          <template v-else>Mehrfachauswahl</template>
        </MedButton>
      </template>
    </template>
  </div>

  <ComponentAlert v-if="problems.unloadableLaborvalues?.length > 0" type="warning" :test-id="getTestId('bar')">
    <h4 class="font-bold">
      In diesem Bereich befinden sich Labor- / Vitalwerte, die in der aktualisierten Datenbank ersetzt wurden.
    </h4>

    <div v-if="problems.unloadableLaborvalues?.length > 0">
      <p class="mt-2">
        Bitte tauschen Sie folgende Labor- / Vitalwerte durch geeignete Neue aus, damit sie im Check erkannt und
        berücksichtigt werden:
      </p>

      <ul class="mt-2 list-disc space-y-1 pl-6">
        <li v-for="(item, index) in problems.unloadableLaborvalues" :key="'unsolved-' + index">
          <em>{{ item.type }}</em>
        </li>
      </ul>
    </div>
  </ComponentAlert>

  <div v-if="isGenderDiverse" class="px-4">
    <ComponentAlert type="warning" :test-id="getTestId('bar')">
      <p>
        Eine Überprüfung der Labor-/Vitalwerte ist für das für den Patienten hinterlegten Geschlecht "divers" nicht
        möglich, es werden daher die Referenzbereiche für das Geschlecht "weiblich" verwendet.
      </p>
    </ComponentAlert>
  </div>

  <section ref="refLaborBar">
    <ComponentAlert v-if="mainLaborValuesWithConflicts.length > 0" type="error">
      <p>In den Standardlaborwerten dürfen nach dem Zusammenführen keine doppelten Einträge enthalten sein.</p>
      <ul class="mt-2 ml-8 list-disc">
        <li v-for="type in mainLaborValuesWithConflicts" :key="type">{{ type }}</li>
      </ul>
    </ComponentAlert>

    <!-- Main labor values -->
    <MainLaborValues
      :labor-values="mainLaborValues"
      :is-editable="isEditable && !isMassUpdateProcessing"
      :test-id="getTestId('bar')"
      :mode="modeOfMainLaborValues"
      @update="updateLaborValues"
    />

    <div v-if="isEditable || otherLaborValues.length > 0" class="mt-4 flex items-center space-x-2 px-2 pb-4">
      <h6 class="px-2 text-xl">Weitere Labor- / Vitalwerte</h6>

      <template v-if="(isEditable && otherLaborValues?.length >= 2) || modeOfOtherLaborValues === 'multi-select'">
        <MedButton
          class="ml-auto"
          :data-test="getTestId('toggle-multi-select-button')"
          @click="toggleModeOfOtherLaborValues('multi-select')"
        >
          <template v-if="modeOfOtherLaborValues === 'multi-select'">Abbrechen</template>
          <template v-else>Mehrfachauswahl</template>
        </MedButton>
      </template>
    </div>

    <ComponentAlert v-if="otherLaborValuesWithConflicts.length > 0" type="warning">
      <p>
        Die Laborwerte sind widersprüchlich, daher können keine passenden Symptome/Erkrankungen abgeleitet werden. Bitte
        überprüfen oder korrigieren Sie die Angaben in allen betroffenen Bereichen (Symptome / Erkrankungen /
        Laborwerte) für:
      </p>

      <ul class="mt-2 ml-8 list-disc">
        <li v-for="type in otherLaborValuesWithConflicts" :key="type">{{ type }}</li>
      </ul>
    </ComponentAlert>

    <!-- Table -->
    <template v-if="otherLaborValues.length > 0">
      <InertiaDeferred data="laborvalues">
        <template #fallback />

        <LaborTable
          :record-labor-values="otherLaborValues"
          :is-editable="isEditable"
          :test-id="getTestId('bar')"
          :mode="modeOfOtherLaborValues"
          @update="updateLaborValues"
        />
      </InertiaDeferred>
    </template>
  </section>

  <!-- Search -->
  <LaborSearch
    v-if="isEditable"
    :labor-values="laborValues"
    :disabled="isMassUpdateProcessing"
    :test-id="getTestId('bar')"
  />

  <!-- Dialogs -->
  <template v-if="isEditable">
    <LaborTableGfrCalculator ref="refLaborValueGfrDialog" :test-id="getTestId('bar')" />
  </template>
</template>
